// pms-adapters / supabase / bankTransfer — a property's bank-transfer instructions (PB10, migration 20261101100100).
//
// DIRECT under RLS, as for the cancellation policies: the IBAN's mod-97 check, the BIC's shape and the delay are the
// table's own CHECKs, so the database refuses a bad row whatever writes it. One row per établissement.

import {
  err,
  ok,
  normalizeBic,
  normalizeIban,
  type BankTransferDetails,
  type BankTransferRepository,
  type Result,
  type ScopeSource,
} from '@lemontaigu/pms-core';
import { fromList, fromVoid, scopedInsert, scopedQuery, type SupabaseClient } from './support.js';

export interface BankTransferRow {
  property_code: string;
  account_holder: string;
  iban: string;
  bic: string;
  due_days: number;
}

const COLUMNS = 'property_code, account_holder, iban, bic, due_days';

export const rowToBankTransfer = (r: BankTransferRow): BankTransferDetails => ({
  propertyCode: r.property_code,
  accountHolder: r.account_holder,
  iban: r.iban,
  bic: r.bic,
  dueDays: r.due_days,
});

export class SupabaseBankTransferRepository implements BankTransferRepository {
  constructor(
    private readonly db: SupabaseClient,
    private readonly scope: ScopeSource,
  ) {}

  async get(): Promise<Result<BankTransferDetails | null>> {
    const r = fromList<BankTransferRow, BankTransferDetails>(
      await scopedQuery(this.db.from('pms_property_bank').select(COLUMNS), this.scope.current()).order('property_code'),
      rowToBankTransfer,
    );
    return r.ok ? ok(r.value[0] ?? null) : r;
  }

  async save(details: Omit<BankTransferDetails, 'propertyCode'>): Promise<Result<void>> {
    // One établissement's row: never written without knowing which.
    const scoped = scopedInsert(this.scope.current());
    if (scoped['property_code'] === undefined) return err('validation', 'Choisissez d’abord un établissement.');
    return fromVoid(
      await this.db.from('pms_property_bank').upsert(
        {
          ...scoped,
          account_holder: details.accountHolder.trim(),
          // Stored compact and upper case: the table's CHECK reads that form.
          iban: normalizeIban(details.iban),
          bic: normalizeBic(details.bic),
          due_days: details.dueDays,
        },
        { onConflict: 'property_code' },
      ),
    );
  }
}
